import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";

import "./AppHeader.css";

// =========================================================
// HEADER IMAGES
// =========================================================

import notificationIcon from "../../assets/images/header_images/notification.png";
import profileIcon from "../../assets/images/header_images/user.png";
import logoutIcon from "../../assets/images/header_images/log-out.png";
import syncIcon from "../../assets/images/header_images/refresh.png";

// =========================================================
// LOTTIE
// =========================================================

import Gold_Coin_Lottie from "../../assets/animation/gold_coin_lottie/Gold_Coin_Lottie";
import Refresh_Lottie from "../../assets/animation/refresh_lottie/Refresh_Lottie";

// =========================================================
// MODALS
// =========================================================

import LogoutConfirmationModal from "../../components/header/logout/logoutConfirmationModal/LogoutConfirmationModal";

import TodaysCashCollectionModal from "../../components/header/logout/todaysCashCollectionModal/TodaysCashCollectionModal";

import ReceiveCashModal from "../../components/header/logout/receiveCashModal/ReceiveCashModal";

import StatusModal from "../../common/common_modal/statusModal/StatusModal";

// =========================================================
// COMPONENT
// =========================================================

const AppHeader = () => {
  const navigate = useNavigate();

  // =========================================================
  // MODAL STATES
  // =========================================================

  /*
   * 1. Logout Confirmation Modal
   */
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  /*
   * 2. Today's Cash Collection Modal
   */
  const [
    showTodaysCashCollectionModal,
    setShowTodaysCashCollectionModal,
  ] = useState(false);

  /*
   * 3. Receive Cash Modal
   */
  const [showReceiveCashModal, setShowReceiveCashModal] =
    useState(false);

  /*
   * 4. Cash Collected Modal
   */
  const [showCashCollectedModal, setShowCashCollectedModal] =
    useState(false);

  /*
   * 5. Logout Done Modal
   */
  const [showLogoutSuccessModal, setShowLogoutSuccessModal] =
    useState(false);

  /*
   * 6. Refresh Lottie
   */
  const [showRefreshLottie, setShowRefreshLottie] =
    useState(false);

  // =========================================================
  // LOGOUT BUTTON
  // =========================================================

  /*
   * Header Logout click
   *
   * Open Logout Confirmation Modal
   */

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // =========================================================
  // LOGOUT CONFIRMATION MODAL
  // =========================================================

  /*
   * Close Logout Confirmation Modal
   */

  const handleLogoutModalClose = () => {
    setShowLogoutModal(false);
  };

  /*
   * Logout Confirmation
   *
   * Proceed
   *
   * ↓
   *
   * Today's Cash Collection
   */

  const handleLogoutProceed = () => {
    setShowLogoutModal(false);

    setTimeout(() => {
      setShowTodaysCashCollectionModal(true);
    }, 150);
  };

  // =========================================================
  // TODAY'S CASH COLLECTION
  // =========================================================

  /*
   * Today's Cash Collection
   *
   * YES
   *
   * ↓
   *
   * Cash Collected
   */

  const handleTodaysCashYes = () => {
    setShowTodaysCashCollectionModal(false);

    setTimeout(() => {
      setShowCashCollectedModal(true);
    }, 150);
  };

  /*
   * Today's Cash Collection
   *
   * NO
   *
   * ↓
   *
   * Receive Cash
   */

  const handleTodaysCashNo = () => {
    setShowTodaysCashCollectionModal(false);

    setTimeout(() => {
      setShowReceiveCashModal(true);
    }, 150);
  };

  /*
   * Close Today's Cash Collection Modal
   */

  const handleTodaysCashCollectionClose = () => {
    setShowTodaysCashCollectionModal(false);
  };

  // =========================================================
  // RECEIVE CASH
  // =========================================================

  /*
   * Header Receive Today's Cash button
   *
   * Open Receive Cash Modal
   */

  const handleReceiveCashClick = () => {
    setShowReceiveCashModal(true);
  };

  /*
   * Close Receive Cash Modal
   */

  const handleReceiveCashClose = () => {
    setShowReceiveCashModal(false);
  };

  // =========================================================
  // PAYMENT CONFIRMATION
  // =========================================================

  /*
   * Payment Confirmation
   *
   * YES
   *
   * ↓
   *
   * Cash Collected
   */

  const handlePaymentYes = () => {
    setShowReceiveCashModal(false);

    setTimeout(() => {
      setShowCashCollectedModal(true);
    }, 150);
  };

  /*
   * Payment Confirmation
   *
   * NO
   *
   * ↓
   *
   * Today's Cash Collection
   */

  const handlePaymentNo = () => {
    setShowReceiveCashModal(false);

    setTimeout(() => {
      setShowTodaysCashCollectionModal(true);
    }, 150);
  };

  // =========================================================
  // CASH COLLECTED
  // =========================================================

  /*
   * Cash Collected
   *
   * DONE
   *
   * ↓
   *
   * Logout Done
   */

  const handleCashCollectedDone = () => {
    setShowCashCollectedModal(false);

    setTimeout(() => {
      setShowLogoutSuccessModal(true);
    }, 150);
  };

  /*
   * Close Cash Collected Modal
   */

  const handleCashCollectedClose = () => {
    setShowCashCollectedModal(false);
  };

  // =========================================================
  // LOGOUT DONE
  // =========================================================

  /*
   * Logout Done Modal
   *
   * Automatically close after 3 seconds
   */

  useEffect(() => {
    if (!showLogoutSuccessModal) {
      return;
    }

    const logoutTimer = setTimeout(() => {
      setShowLogoutSuccessModal(false);

      // Clear storage
      localStorage.clear();
      sessionStorage.clear();

      // Navigate to login/home
      navigate("/");
    }, 3000);

    /*
     * Clear timer if component unmounts
     * or modal closes manually.
     */

    return () => {
      clearTimeout(logoutTimer);
    };
  }, [showLogoutSuccessModal, navigate]);

  /*
   * If user clicks Done manually
   */

  const handleLogoutSuccessDone = () => {
    setShowLogoutSuccessModal(false);

    // Clear storage
    localStorage.clear();
    sessionStorage.clear();

    // Navigate
    navigate("/");
  };

  /*
   * Close Logout Done Modal
   */

  const handleLogoutSuccessClose = () => {
    setShowLogoutSuccessModal(false);
  };

  // =========================================================
  // REFRESH
  // =========================================================

  const handleRefresh = () => {
    setShowRefreshLottie(true);
  };

  /*
   * Automatically hide refresh animation
   */

  useEffect(() => {
    if (!showRefreshLottie) {
      return;
    }

    const refreshTimer = setTimeout(() => {
      setShowRefreshLottie(false);
    }, 2500);

    return () => {
      clearTimeout(refreshTimer);
    };
  }, [showRefreshLottie]);

  // =========================================================
  // JSX
  // =========================================================

  return (
    <>
      {/* =====================================================
          APP HEADER
      ===================================================== */}

      <header className="app-header">

        {/* ===================================================
            LEFT SECTION
        =================================================== */}

        <div className="header-left">

          <h1 className="user-greeting">
            Hello, Store Manager
          </h1>

          <div className="store-address">

            <FontAwesomeIcon
              icon={faMapMarkerAlt}
              className="location-icon"
            />

            <span className="address-text">
              Shop 30, Avenue 93 Mall, Near Croma Mall,
              Fatima Nagar, Kalubai Chowk, Pune,
              Maharashtra
            </span>

          </div>

        </div>

        {/* ===================================================
            RIGHT SECTION
        =================================================== */}

        <div className="header-right">

          {/* =================================================
              RECEIVE TODAY'S CASH
          ================================================= */}

          <button
            type="button"
            className="cash-btn"
            onClick={handleReceiveCashClick}
          >

            <div className="left_lotties_div">
              <Gold_Coin_Lottie />
            </div>

            <div className="text_holder">

              <p className="first_text">
                Receive
              </p>

              <p className="first_text">
                Todays cash
              </p>

            </div>

          </button>

          {/* =================================================
              HEADER ICONS
          ================================================= */}

          <div className="header-icons">

            {/* NOTIFICATION */}

            <Link to="/Notification">

              <button
                type="button"
                className="icon-btn"
                title="Notifications"
              >

                <img
                  src={notificationIcon}
                  alt="Notification"
                  className="action-icon"
                />

              </button>

            </Link>

            {/* PROFILE */}

            <Link to="/profile">

              <button
                type="button"
                className="icon-btn"
                title="Profile"
              >

                <img
                  src={profileIcon}
                  alt="Profile"
                  className="action-icon"
                />

              </button>

            </Link>

            {/* LOGOUT */}

            <button
              type="button"
              className="icon-btn"
              title="Logout"
              onClick={handleLogoutClick}
            >

              <img
                src={logoutIcon}
                alt="Logout"
                className="action-icon"
              />

            </button>

            {/* REFRESH */}

            <button
              type="button"
              className="icon-btn"
              title="Refresh"
              onClick={handleRefresh}
            >

              <img
                src={syncIcon}
                alt="Refresh"
                className="action-icon"
              />

            </button>

          </div>

        </div>

      </header>

      {/* =====================================================
          REFRESH LOTTIE
      ===================================================== */}

      {showRefreshLottie && (

        <div className="refresh-page-overlay">

          <div className="refresh-page-lottie">
            <Refresh_Lottie />
          </div>

        </div>

      )}

      {/* =====================================================
          1. LOGOUT CONFIRMATION MODAL
      ===================================================== */}

      <LogoutConfirmationModal
        show={showLogoutModal}
        onHide={handleLogoutModalClose}
        onButtonClick={handleLogoutProceed}
      />

      {/* =====================================================
          2. TODAY'S CASH COLLECTION MODAL
      ===================================================== */}

      <TodaysCashCollectionModal
        show={showTodaysCashCollectionModal}
        onHide={handleTodaysCashCollectionClose}
        onNoButtonClick={handleTodaysCashNo}
        onYesButtonClick={handleTodaysCashYes}
      />

      {/* =====================================================
          3. RECEIVE CASH MODAL
      ===================================================== */}

      <ReceiveCashModal
        show={showReceiveCashModal}
        onHide={handleReceiveCashClose}

        /*
         * Payment Confirmation -> YES
         * Cash Collected
         */

        onPaymentYes={handlePaymentYes}

        /*
         * Payment Confirmation -> NO
         * Today's Cash Collection
         */

        onPaymentNo={handlePaymentNo}
      />

      {/* =====================================================
          4. CASH COLLECTED MODAL
      ===================================================== */}

      <StatusModal
        show={showCashCollectedModal}
        onHide={handleCashCollectedClose}
        title="Cash Collected"
        statusTitle="Today's Cash Collected"
        message="Today's cash collection has been completed successfully."
        buttonText="Done"
        onButtonClick={handleCashCollectedDone}
      />

      {/* =====================================================
          5. LOGOUT DONE MODAL
      ===================================================== */}

      <StatusModal
        show={showLogoutSuccessModal}
        onHide={handleLogoutSuccessDone}
        title="Logout Done"
        statusTitle="Logged Out Successfully"
        message="You've been logged out successfully. See you again soon!"
        buttonText="Done"
        onButtonClick={handleLogoutSuccessDone}
      />

    </>
  );
};

export default AppHeader;